// src/services/print/PrintService.ts
import { renderToString } from 'react-dom/server';
import { DetalleVenta } from '../../../components/types/ventas.types';
import { FacturaCompletaPDF } from '../../../components/module/ventas/facturas/FacturaCompletaPDF';
import { TicketPOS } from '../../../components/module/ventas/facturas/TicketPOS';
import { formatDate, formatMoney } from '../../../utils/format';
import ReactPDF, { pdf, Document } from '@react-pdf/renderer';
import React from 'react';
import axios from 'axios';

export enum TipoImpresion {
    FACTURA_COMPLETA = 'FACTURA_COMPLETA',
    TICKET_POS = 'TICKET_POS'
}

export enum TipoImpresora {
    TERMICA = 'TERMICA',
    LASER = 'LASER',
    POS = 'POS'
}

interface OpcionesImpresion {
    tipo: TipoImpresion;
    impresora: TipoImpresora;
    copias?: number;
    configuracionEmpresa: {
        nombre: string;
        nit: string;
        direccion: string;
        telefono: string;
        email: string;
        sitioWeb?: string;
    };
}

interface ConfiguracionEmpresaProps {
    nombre: string;
    nit: string;
    direccion: string;
    telefono: string;
    email: string;
    sitioWeb?: string;
}

// Interface base para la configuración de la empresa
interface ConfiguracionEmpresaBase {
    nombre: string;
    nit: string;
    direccion: string;
    telefono: string;
}

// Extender la configuración base para factura completa
interface ConfiguracionEmpresaCompleta extends ConfiguracionEmpresaBase {
    email: string;
    sitioWeb?: string;
}

// Props base para ambos componentes
interface PDFBaseProps {
    venta: DetalleVenta;
}

// Props específicos para FacturaCompleta
interface FacturaCompletaPDFProps extends PDFBaseProps {
    configuracionEmpresa: ConfiguracionEmpresaCompleta;
}

// Props específicos para TicketPOS
interface TicketPOSProps extends PDFBaseProps {
    configuracionEmpresa: ConfiguracionEmpresaBase;
}

export interface Impresora {
    nombre: string;
    puerto: string;
    tipo: 'LASER' | 'POS' | 'TERMICA';
    predeterminada: boolean;
  }
  
  class ImpresorasService {
    async obtenerImpresoras(): Promise<Impresora[]> {
      try {
        const response = await axios.get('/impresoras');
        
        if (response.data.success) {
          return response.data.data;
        }
        
        throw new Error(response.data.error || 'Error al obtener impresoras');
      } catch (error) {
        console.error('Error obteniendo impresoras:', error);
        throw error;
      }
    }

    filtrarImpresorasPorTipo(impresoras: Impresora[], tipo: TipoImpresion) {
        if (tipo === TipoImpresion.TICKET_POS) {
          return impresoras.filter(imp => imp.tipo === TipoImpresora.POS);
        }
        return impresoras.filter(imp => imp.tipo === TipoImpresora.LASER);
      }
  }

class PrintService {

    async imprimirVenta(venta: DetalleVenta, opciones: OpcionesImpresion) {
        // Si es impresora POS, usar comunicación directa
        if (opciones.impresora === TipoImpresora.POS) {
            return await this.imprimirEnPOS(venta, opciones);
        }

        // Para otros tipos, generar PDF
        const pdfBlob = await this.generarPDF(venta, opciones);
        return await this.enviarAImpresora(pdfBlob, opciones);
    }

    private async generarPDF(venta: DetalleVenta, opciones: OpcionesImpresion): Promise<Blob> {
        try {
            let PDFDocument;

            if (opciones.tipo === TipoImpresion.FACTURA_COMPLETA) {
                PDFDocument = React.createElement(Document, {},
                    React.createElement(FacturaCompletaPDF, {
                        venta,
                        configuracionEmpresa: opciones.configuracionEmpresa as ConfiguracionEmpresaCompleta
                    })
                );
            } else {
                PDFDocument = React.createElement(Document, {},
                    React.createElement(TicketPOS, {
                        venta,
                       /*  configuracionEmpresa: {
                            nombre: opciones.configuracionEmpresa.nombre,
                            nit: opciones.configuracionEmpresa.nit,
                            direccion: opciones.configuracionEmpresa.direccion,
                            telefono: opciones.configuracionEmpresa.telefono
                        } */
                    })
                );
            }

            // Generar el blob del PDF
            const blob = await pdf(PDFDocument).toBlob();
            return blob;
        } catch (error) {
            console.error('Error generando PDF:', error);
            throw new Error('Error al generar el PDF');
        }
    }

    private async imprimirEnPOS(venta: DetalleVenta, opciones: OpcionesImpresion) {
        try {
            const datos = this.formatearParaPOS(venta);
            const response = await fetch('http://localhost:9100', {
                method: 'POST',
                body: JSON.stringify(datos),
                headers: {
                    'Content-Type': 'application/json'
                }
            });

            if (!response.ok) {
                throw new Error('Error al imprimir en POS');
            }

            return true;
        } catch (error) {
            console.error('Error imprimiendo en POS:', error);
            throw error;
        }
    }

    private async enviarAImpresora(pdfBlob: Blob, opciones: OpcionesImpresion) {
        try {
            const formData = new FormData();
            formData.append('pdf', pdfBlob);
            formData.append('printer', opciones.impresora);
            formData.append('copies', opciones.copias?.toString() || '1');

            const response = await fetch('/api/print', {
                method: 'POST',
                body: formData
            });

            if (!response.ok) {
                throw new Error('Error al enviar a la impresora');
            }

            return true;
        } catch (error) {
            console.error('Error enviando a impresora:', error);
            throw error;
        }
    }

    private formatearParaPOS(venta: DetalleVenta): string {
        let comando = '';

        // Inicializar impresora
        comando += '\x1B\x40';

        // Configurar tamaño de texto
        comando += '\x1B\x21\x00';

        // Centrar texto
        comando += '\x1B\x61\x01';

        // Agregar encabezado
        comando += `${venta.sucursal.nombre}\n`;
        comando += `FACTURA N° ${venta.numeroFactura}\n`;
        comando += `Fecha: ${formatDate(venta.fecha)}\n\n`;

        // Alinear a la izquierda
        comando += '\x1B\x61\x00';

        // Datos del cliente
        comando += `Cliente: ${venta.cliente.nombre}\n`;
        comando += `NIT/CC: ${venta.cliente.identificacion}\n\n`;

        // Artículos
        comando += '-'.repeat(40) + '\n';
        comando += 'ARTICULO          CANT   PRECIO   TOTAL\n';
        comando += '-'.repeat(40) + '\n';

        venta.articulos.forEach((articulo: { Nombre: string; Cantidad: number; PrecioVenta: number; Valor: number }) => {
            comando += `${articulo.Nombre.substring(0, 15).padEnd(15)} `;
            comando += `${articulo.Cantidad.toString().padStart(4)} `;
            comando += `${formatMoney(articulo.PrecioVenta).padStart(8)} `;
            comando += `${formatMoney(articulo.Valor).padStart(8)}\n`;
        });

        comando += '-'.repeat(40) + '\n\n';

        // Totales
        comando += `Subtotal: ${formatMoney(venta.subtotal).padStart(20)}\n`;
        if (venta.descuentoTotal > 0) {
            comando += `Descuento: ${formatMoney(venta.descuentoTotal).padStart(19)}\n`;
        }
        venta.impuestos.forEach((impuesto: { nombre: string; valor: number }) => {
            comando += `${impuesto.nombre}: ${formatMoney(impuesto.valor).padStart(22)}\n`;
        });
        comando += `TOTAL: ${formatMoney(venta.totalAPagar).padStart(24)}\n\n`;

        // Pie de página
        comando += '\x1B\x61\x01';
        comando += '¡Gracias por su compra!\n\n';

        // Cortar papel
        comando += '\x1D\x56\x41';

        return comando;
    }

    private async obtenerConfiguracionImpresora(sucursalId: number): Promise<any> {
        try {
            const response = await fetch(`/api/sucursales/${sucursalId}/configuracion-impresora`);
            if (!response.ok) {
                throw new Error('Error obteniendo configuración de impresora');
            }
            return await response.json();
        } catch (error) {
            console.error('Error:', error);
            throw error;
        }
    }
}

export const printService = new PrintService();
export const impresorasService = new ImpresorasService();